import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { ImageWithPreview } from './types';

export const useImageUpload = (onImagesSelected: (files: Array<{ file: File; keywords: string }>) => void) => {
  const [images, setImages] = useState<ImageWithPreview[]>([]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 50) {
      toast.error('Maximum 50 images allowed at once');
      acceptedFiles = acceptedFiles.slice(0, 50);
    }

    const validFiles = acceptedFiles.filter(file => {
      const isValid = file.size <= 5 * 1024 * 1024 && 
                     ['image/jpeg', 'image/png', 'image/webp'].includes(file.type);
      if (!isValid) {
        toast.error(`${file.name} is invalid. Please ensure it's under 5MB and in JPEG, PNG, or WebP format.`);
      }
      return isValid;
    });

    if (validFiles.length > 0) {
      const newImages = validFiles.map(file => ({
        file,
        preview: URL.createObjectURL(file),
        keywords: ''
      }));
      
      if (images.length + newImages.length > 50) {
        toast.error('Maximum 50 images allowed in total');
        const remainingSlots = Math.max(0, 50 - images.length);
        newImages.splice(remainingSlots);
      }
      
      setImages(prev => {
        const updatedImages = [...prev, ...newImages];
        updateSelectedImages(updatedImages);
        return updatedImages;
      });
    }
  }, [images]);

  const removeImage = (index: number) => {
    URL.revokeObjectURL(images[index].preview);
    setImages(prev => {
      const newImages = prev.filter((_, i) => i !== index);
      updateSelectedImages(newImages);
      return newImages;
    });
  };

  const updateKeywords = (index: number, keywords: string) => {
    setImages(prev => {
      const newImages = [...prev];
      newImages[index].keywords = keywords;
      updateSelectedImages(newImages);
      return newImages;
    });
  };

  const updateSelectedImages = (images: ImageWithPreview[]) => {
    onImagesSelected(images.map(({ file, keywords }) => ({ file, keywords })));
  };

  return {
    images,
    onDrop,
    removeImage,
    updateKeywords
  };
};